/* eslint-disable */
import { Dispatch, SetStateAction, memo, useCallback, useEffect } from "react";
import { FormikProps, getIn } from "formik";
import CustomTextField from "../../../../../shared/CustomTextField";
import { GeneralDetails, PRODUCT_ID } from "src/helpers/FormDetails/ProposalDetails/interfaces";
import CustomDatePicker from "src/shared/CustomDatePicker";
import CustomRadioButton from "src/shared/CustomRadioButton";
import { useSelector } from "react-redux";
import { Button } from "@mui/material";
import Resources from "src/config/Resources";
import {
  addOrdinalSuffix,
  findKeysByValue
} from "src/helpers/FormDetails/ProposalDetails/resources";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import CustomErrorText from "src/shared/CustomErrorText";
import { VariableConstants, labels, partners } from "src/config/constant";

interface Props {
  formik: FormikProps<GeneralDetails>;
  proportionateNumber: number;
  onRemoveClick: any;
  value: any;
  key: string;
  proportionateError: {
    get: boolean[];
    set: Dispatch<SetStateAction<boolean[]>>;
  };
}

const combinedSelector = (state: { masterdata_reducer: { genderDetails: any } }) => {
  const genderData = state.masterdata_reducer.genderDetails;
  let genderDataList: string[] = genderData ? Object.values(genderData) : [];

  return {
    genderDataList
  };
};

function ProportionateLife({
  formik: { errors, handleBlur, setFieldTouched, touched, values, setFieldValue, handleChange },
  proportionateNumber,
  onRemoveClick,
  key,
  proportionateError
}: Props) {
  const mefSchema = useAppSelector((state): any => state.masterdata_reducer?.mefJourneySchema);
  const proportionateFields = mefSchema?.generalProposerDetailsSchema?.proportionate || {};
  const masterData = useAppSelector((state) => state.masterdata_reducer);
  const { genderDataList } = useSelector(combinedSelector);
  const productId = useAppSelector((state) => state.common.productId);
  const partnerId = useAppSelector((state) => state?.common?.leadDetails?.partnerId);

  useEffect(() => {
    const proprtionateGender =
      values?.proportionate?.[proportionateNumber]?.proprtionateGender ?? "";
    setFieldValue(
      `proportionate[${proportionateNumber}].genderCode`,
      findKeysByValue(masterData?.genderDetails, proprtionateGender)
    );
  }, [values?.proportionate?.[proportionateNumber]?.proprtionateGender]);

  const handleSumAssuredChange = (e: any) => {
    handleChange(e);

    const sumAssured = Number(e.target.value || 0);
    const loanAmount = Number(values?.totalLoanAmountOpted || 0);

    if (loanAmount > 0 && sumAssured > 0) {
      const percent = (sumAssured / loanAmount) * 100;
      setFieldValue(
        `proportionate[${proportionateNumber}].proprtionatePercent`,
        Number(percent.toFixed(2))
      );
    } else {
      setFieldValue(`proportionate[${proportionateNumber}].proprtionatePercent`, "");
    }
  };

  const checkIfErrorInSumAssured = useCallback(() => {
    const sumAssured = Number(
      values?.proportionate?.[proportionateNumber]?.proprtionateSumAssured || 0
    );

    if (partnerId === partners.JIO) return sumAssured < VariableConstants.JFS_MIN_LOAN;
    else if ([PRODUCT_ID.GHS, PRODUCT_ID.GCS].includes(productId!)) {
      return sumAssured < VariableConstants.GHS_MIN_LOAN;
    } else {
      return sumAssured < VariableConstants.BISMINLOAN;
    }
  }, [
    values?.proportionate?.[proportionateNumber]?.proprtionateSumAssured,
    values?.totalLoanAmountOpted
  ]);

  useEffect(() => {
    const saveErrorStatus =
      !!values?.proportionate?.[proportionateNumber]?.proprtionateSumAssured &&
      checkIfErrorInSumAssured();
    proportionateError.set((prev) => {
      const newState = [...prev];
      newState[proportionateNumber] = saveErrorStatus;
      return newState;
    });
  }, [
    values?.proportionate?.[proportionateNumber]?.proprtionateSumAssured,
    values?.totalLoanAmountOpted
  ]);
  return (
    <div key={key}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex flex-col">
          <span className="font-montserrat text-black text-base font-bold !my-auto">
            {`${
              proportionateNumber > 0 ? addOrdinalSuffix(proportionateNumber + 1) : ""
            } Proportionate Details`}
          </span>
          {proportionateError.get[proportionateNumber] && (
            <span>
              <CustomErrorText
                text={
                  partnerId === partners.SREI
                    ? labels?.proportionateEligibility?.text_SREI
                    : partnerId === partners.JIO
                    ? labels?.proportionateEligibility?.text_JFS
                    : productId === PRODUCT_ID.GHS || productId === PRODUCT_ID.GCS
                    ? labels?.proportionateEligibility?.text_GHS
                    : labels?.proportionateEligibility?.text_GLS
                }
                type="error"
              />
            </span>
          )}
        </div>
        {proportionateNumber > 0 && (
          <Button
            variant="outlined"
            onClick={onRemoveClick}
            classes={{ root: "!px-0" }}
            className="!text-black !uppercase h-10 !rounded-lg !border-2 !mr-10"
          >
            <img src={Resources.images.FormDetails.removeIcon} alt="remove" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6 mb-6">
        {proportionateFields?.nameOfProportionateLife?.visible && (
          <CustomTextField
            requiredStar
            label={proportionateFields?.nameOfProportionateLife?.label}
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values?.proportionate?.[proportionateNumber].nameOfProportionateLife ?? ""}
            name={`proportionate[${proportionateNumber}].nameOfProportionateLife`}
            error={getIn(errors, `proportionate[${proportionateNumber}].nameOfProportionateLife`)}
            touched={getIn(
              touched,
              `proportionate[${proportionateNumber}].nameOfProportionateLife`
            )}
          />
        )}
        {proportionateFields?.proprtionateDob?.visible && (
          <CustomDatePicker
            disableFuture
            requiredStar
            label={proportionateFields?.proprtionateDob?.label}
            setFieldValue={setFieldValue}
            name={`proportionate[${proportionateNumber}].proprtionateDob`}
            value={values?.proportionate?.[proportionateNumber].proprtionateDob ?? ""}
            error={getIn(errors, `proportionate[${proportionateNumber}].proprtionateDob`)}
            touched={getIn(touched, `proportionate[${proportionateNumber}].proprtionateDob`)}
            setFieldTouched={setFieldTouched}
          />
        )}

        <CustomTextField
          requiredStar
          label={"Sum Assured"}
          onChange={handleSumAssuredChange}
          handleBlur={handleBlur}
          value={values?.proportionate?.[proportionateNumber]?.proprtionateSumAssured ?? ""}
          name={`proportionate[${proportionateNumber}].proprtionateSumAssured`}
          error={getIn(errors, `proportionate[${proportionateNumber}].proprtionateSumAssured`)}
          touched={getIn(touched, `proportionate[${proportionateNumber}].proprtionateSumAssured`)}
        />
        {proportionateFields?.proprtionateGender?.visible && (
          <CustomRadioButton
            requiredStar
            label={proportionateFields?.proprtionateGender?.label}
            className="col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3"
            options={genderDataList}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            defaultValue={values?.proportionate?.[proportionateNumber]?.proprtionateGender ?? ""}
            name={`proportionate[${proportionateNumber}].proprtionateGender`}
            error={getIn(errors, `proportionate[${proportionateNumber}].proprtionateGender`)}
            touched={getIn(touched, `proportionate[${proportionateNumber}].proprtionateGender`)}
          />
        )}
      </div>
    </div>
  );
}

export default memo(ProportionateLife);
